import { useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import GalleryImageViewer from "./GalleryImageViewer";

export default function GalleryImagesPanel({
  galleryId,
  galleryName,
  images,
  loading,
  reloadImages,
  onMessageChange,
  request,
}) {
  const inputRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageToDelete, setImageToDelete] = useState(null);

  const hasImages = Array.isArray(images) && images.length > 0;

  const openViewer = (index) => {
    setCurrentIndex(index);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
  };

  const handleUploadClick = () => {
    if (!galleryId || uploading) return;
    inputRef.current?.click();
  };

  const handleUpload = async (fileList) => {
    if (!galleryId || !fileList?.length) return;

    const form = new FormData();

    Array.from(fileList).forEach((file) => {
      form.append("images", file);
    });

    setUploading(true);
    onMessageChange({ type: "", text: "" });

    try {
      await request(`/gallery/admin/upload/${galleryId}/`, {
        method: "POST",
        body: form,
      });

      onMessageChange({
        type: "success",
        text: `${fileList.length} imagen${
          fileList.length === 1 ? "" : "es"
        } subida${fileList.length === 1 ? "" : "s"} correctamente`,
      });

      await reloadImages();

      if (inputRef.current) {
        inputRef.current.value = "";
      }
    } catch (error) {
      onMessageChange({ type: "error", text: error.message });
    } finally {
      setUploading(false);
    }
  };

  const openDeleteDialog = (image, index) => {
    setImageToDelete({ ...image, index });
  };

  const closeDeleteDialog = () => {
    if (deletingId) return;
    setImageToDelete(null);
  };

  const confirmDeleteImage = async () => {
    if (!imageToDelete) return;

    setDeletingId(imageToDelete.id);
    onMessageChange({ type: "", text: "" });

    try {
      await request(`/gallery/admin/delete-image/${imageToDelete.id}/`, {
        method: "DELETE",
      });

      onMessageChange({
        type: "success",
        text: "Imagen eliminada correctamente",
      });

      if (viewerOpen && currentIndex === imageToDelete.index) {
        setViewerOpen(false);
      }

      closeDeleteDialog();
      await reloadImages();
    } catch (error) {
      onMessageChange({ type: "error", text: error.message });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Card
        elevation={0}
        sx={{
          borderRadius: "20px",
          border: "1px solid rgba(140,15,44,0.12)",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={2.2}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <Stack spacing={0.6}>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 800,
                    color: "text.primary",
                  }}
                >
                  Imágenes de la galería
                </Typography>

                <Typography
                  sx={{
                    fontSize: "0.92rem",
                    color: "text.secondary",
                    fontWeight: 500,
                  }}
                >
                  {galleryId
                    ? ""
                    : "Selecciona una galería para empezar."}
                </Typography>
              </Stack>

              <Box>
                <input
                  ref={inputRef}
                  hidden
                  multiple
                  type="file"
                  accept="image/*,.heic,.heif"
                  onChange={(e) => handleUpload(e.target.files)}
                />

                <Button
                  variant="contained"
                  startIcon={
                    uploading ? (
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                    ) : (
                      <CloudUploadRoundedIcon />
                    )
                  }
                  disabled={!galleryId || uploading}
                  onClick={handleUploadClick}
                  sx={{
                    minHeight: 44,
                    borderRadius: "12px",
                    minWidth: { xs: "100%", sm: 220 },
                  }}
                >
                  {uploading ? "Subiendo imágenes..." : "Subir imágenes"}
                </Button>
              </Box>
            </Stack>

            {!galleryId && (
              <Box
                sx={{
                  borderRadius: "18px",
                  border: "1px dashed rgba(140,15,44,0.22)",
                  px: 2,
                  py: 5,
                  textAlign: "center",
                  backgroundColor: "rgba(140,15,44,0.02)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.96rem",
                    color: "text.secondary",
                    fontWeight: 700,
                  }}
                >
                  No hay una galería seleccionada
                </Typography>
              </Box>
            )}

            {galleryId && loading && (
              <Box
                sx={{
                  borderRadius: "18px",
                  border: "1px dashed rgba(140,15,44,0.22)",
                  px: 2,
                  py: 6,
                  textAlign: "center",
                  backgroundColor: "rgba(140,15,44,0.02)",
                }}
              >
                <Stack spacing={1.2} alignItems="center">
                  <CircularProgress />
                  <Typography
                    sx={{
                      fontSize: "0.96rem",
                      color: "text.secondary",
                      fontWeight: 700,
                    }}
                  >
                    Cargando imágenes...
                  </Typography>
                </Stack>
              </Box>
            )}

            {galleryId && !loading && !hasImages && (
              <Box
                sx={{
                  borderRadius: "18px",
                  border: "1px dashed rgba(140,15,44,0.22)",
                  px: 2,
                  py: 5,
                  textAlign: "center",
                  backgroundColor: "rgba(140,15,44,0.02)",
                }}
              >
                <Stack spacing={1.2} alignItems="center">
                  <ImageRoundedIcon color="disabled" sx={{ fontSize: 42 }} />
                  <Typography
                    sx={{
                      fontSize: "0.98rem",
                      fontWeight: 800,
                      color: "text.primary",
                    }}
                  >
                    Esta galería aún no tiene imágenes
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      color: "text.secondary",
                      fontWeight: 500,
                    }}
                  >
                    Súbelas desde el botón superior.
                  </Typography>
                </Stack>
              </Box>
            )}

            {galleryId && !loading && hasImages && (
              <Box className="row g-3">
                {images.map((image, index) => (
                  <Box
                    key={image.id}
                    className="col-12 col-sm-6 col-md-4 col-xl-3"
                  >
                    <Box
                      sx={{
                        borderRadius: "18px",
                        overflow: "hidden",
                        border: "1px solid rgba(140,15,44,0.10)",
                        backgroundColor: "#fff",
                        boxShadow: "0 8px 22px rgba(15,23,42,0.06)",
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          aspectRatio: "1 / 1",
                          overflow: "hidden",
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        <Box
                          component="img"
                          src={image.image}
                          alt=""
                          onClick={() => openViewer(index)}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                            cursor: "zoom-in",
                            transition: "transform 0.25s ease",
                            "&:hover": {
                              transform: "scale(1.03)",
                            },
                          }}
                        />

                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{
                            position: "absolute",
                            left: 10,
                            right: 10,
                            bottom: 10,
                          }}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<VisibilityRoundedIcon />}
                            onClick={() => openViewer(index)}
                            sx={{
                              flex: 1,
                              minHeight: 34,
                              borderRadius: "10px",
                              boxShadow: "none",
                              background: "#fff",
                              color: "#333",
                              border: "#333"
                            }}
                          >
                            Ver
                          </Button>

                          <Button
                            size="small"
                            variant="contained"
                            startIcon={
                              deletingId === image.id ? (
                                <CircularProgress
                                  size={14}
                                  sx={{ color: "#fff" }}
                                />
                              ) : (
                                <DeleteRoundedIcon />
                              )
                            }
                            onClick={() => openDeleteDialog(image, index)}
                            disabled={deletingId === image.id}
                            sx={{
                              flex: 1,
                              minHeight: 34,
                              borderRadius: "10px",
                              boxShadow: "none",
                              backgroundColor: "primary.main",
                              "&:hover": {
                                backgroundColor: "primary.dark",
                              },
                            }}
                          >
                            {deletingId === image.id ? "Eliminando..." : "Eliminar"}
                          </Button>
                        </Stack>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      <GalleryImageViewer
        open={viewerOpen}
        images={images}
        currentIndex={currentIndex}
        onClose={closeViewer}
        onChangeIndex={setCurrentIndex}
        galleryName={galleryName}
      />

      <Dialog
        open={Boolean(imageToDelete)}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "primary.main",
            color: "#fff",
            fontWeight: 700,
            fontSize: "1.02rem",
            mb:1
          }}
        >
          Eliminar imagen
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={1.2}>
            <Typography
              sx={{
                fontSize: "0.95rem",
                color: "text.primary",
                fontWeight: 700,
              }}
            >
              ¿Deseas eliminar esta imagen?
            </Typography>

            <Typography
              sx={{
                fontSize: "0.9rem",
                color: "text.secondary",
                fontWeight: 500,
              }}
            >
              {galleryName
                ? `Se eliminará de la galería "${galleryName}".`
                : "Esta acción no se puede deshacer."}
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2.4,
            gap: 1,
          }}
        >
          <Button
            onClick={closeDeleteDialog}
            variant="outlined"
            disabled={Boolean(deletingId)}
          >
            Cancelar
          </Button>

          <Button
            onClick={confirmDeleteImage}
            variant="contained"
            disabled={Boolean(deletingId)}
            sx={{
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            {deletingId ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}